/**
 * Página de `/revisao` abaixo de 768px. O trilho largo continua em
 * `RevisaoHomeShell`. Aqui a agenda é fita, os filtros escolhidos são fichas,
 * e escolher um baralho continua na folha — a linha só mostra o que já está
 * escolhido. A lista rola; a barra de início fica no fim da coluna.
 */
import { SlidersHorizontal } from "lucide-react";
import AgendaFita from "../AgendaFita";
import BarraDeInicio from "../BarraDeInicio";
import { DIA_FICHA_DIMENSION, type FichaDeFiltro } from "../barraDeInicioContrato";
import RevisaoActiveFilters, {
  type RevisaoActiveFilterToken,
} from "../filtros/RevisaoActiveFilters";
import RevisaoFilterSheet from "../filtros/RevisaoFilterSheet";
import {
  rotuloFiltrosEscolhidos,
  type RevisaoHomeEstreitoProps,
} from "../filtros/revisaoFilterSheetContrato";
import { REVISAO_FILTER_FOCUS_RING, revisaoFilterCx } from "../filtros/tokens";
import RevisaoHero from "./RevisaoHero";

function comoFichaDeFiltro(item: RevisaoActiveFilterToken): FichaDeFiltro | null {
  if (
    item.dimension !== "subjects" &&
    item.dimension !== "keywords" &&
    item.dimension !== "decks" &&
    item.dimension !== DIA_FICHA_DIMENSION
  ) {
    return null;
  }
  return {
    dimension: item.dimension,
    dimensionLabel: item.dimensionLabel,
    value: item.value,
    label: item.label && item.label !== "" ? item.label : item.value,
  };
}

export default function RevisaoHomeEstreito(props: RevisaoHomeEstreitoProps): JSX.Element {
  // A história não monta a casca. Na rota, a coluna cabe entre a App Bar,
  // o padding do conteúdo e a BottomTabBar, e a lista rola acima da barra.
  const altura = props.reservaDaBarraDeAbas
    ? "h-[calc(100dvh-4rem-4.5rem-1.5rem)]"
    : "h-[100dvh]";

  return (
    <>
      <div className={revisaoFilterCx("flex min-h-0 flex-col", altura)}>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-y-auto px-4 pt-4 pb-4">
        <div className="flex items-center justify-between gap-3">
          <h1 className="min-w-0 truncate text-xl font-semibold text-ink">Revisão</h1>
          <button
            type="button"
            onClick={props.onAbrirFolha}
            aria-haspopup="dialog"
            aria-expanded={props.folha.open}
            className={revisaoFilterCx(
              "inline-flex h-10 shrink-0 items-center gap-2 rounded-input border border-edge bg-surface px-3",
              "text-sm font-medium text-ink transition-colors hover:bg-surface-hover",
              REVISAO_FILTER_FOCUS_RING,
            )}
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden />
            {rotuloFiltrosEscolhidos(props.filtrosEscolhidos)}
          </button>
        </div>

        <RevisaoHero leitura={props.leitura} aguardando={props.aguardando} />

        <section className="min-w-0" aria-labelledby="revisao-estreito-agenda">
          <h2 id="revisao-estreito-agenda" className="mb-2 text-sm font-semibold text-ink">
            Próximos dias
          </h2>
          <AgendaFita
            dias={props.agenda}
            diaFixado={props.diaFixado}
            onFixarDia={props.onFixarDia}
            onPraticarDia={props.onPraticarDia}
          />
        </section>

        <RevisaoActiveFilters
          variant="inline"
          items={props.fichas}
          className="min-w-0 shrink-0"
          onRemove={(item) => {
            const ficha = comoFichaDeFiltro(item);
            if (ficha) props.onRemoverFicha(ficha);
          }}
          onClearAll={props.onLimpar}
        />

        <section aria-labelledby="revisao-estreito-baralhos">
          <h2 id="revisao-estreito-baralhos" className="mb-2 text-sm font-semibold text-ink">
            Baralhos
          </h2>
          {props.baralhos.length === 0 ? (
            <p className="text-sm text-ink-secondary">Nenhum baralho disponível.</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {props.baralhos.map((baralho) => {
                const escolhido = props.baralhosSelecionados.includes(baralho.name);
                return (
                  <li
                    key={baralho.name}
                    className={revisaoFilterCx(
                      "flex items-center justify-between gap-3 rounded-card border px-3 py-2.5 text-sm",
                      escolhido
                        ? "border-select/[0.42] bg-select/[0.14] text-select-ink"
                        : "border-edge bg-surface text-ink",
                    )}
                  >
                    <span className="min-w-0 truncate">{baralho.name}</span>
                    <span className="shrink-0 tabular-nums">{baralho.count}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      <BarraDeInicio
        densa
        cartoes={props.leitura.total}
        aguardando={props.aguardando}
        ahead={props.ahead}
        onAhead={props.onAhead}
        onLimpar={props.onLimpar}
        podeLimpar={props.podeLimpar}
        onIniciar={props.onIniciar}
      />
      </div>

      <RevisaoFilterSheet {...props.folha} />
    </>
  );
}
